import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import {
  getApiV1LocationStatesQueryOptions,
  usePostApiV1LocationStates,
  usePutApiV1LocationStatesId,
} from '@/shared/api/generated';
import type { StateFormValues } from '../types/stateForm';

/** Callbacks do salvamento de Estado, com caminho específico para conflito (409). */
export interface SaveStateCallbacks {
  readonly onSuccess?: () => void;
  /** Chamado quando a API retorna 409 (código/sigla já cadastrados). */
  readonly onConflict?: () => void;
  /** Chamado para qualquer outro erro. */
  readonly onError?: () => void;
}

/**
 * Hook para criar/editar um Estado.
 *
 * Encapsula os mutations gerados pelo Kubb (`POST /api/v1/location/states` e
 * `PUT /api/v1/location/states/{id}`), escolhendo a operação pela presença de
 * `id`, invalidando o cache da listagem no sucesso e mapeando o status **409**
 * (código/sigla já cadastrados) para o callback `onConflict`.
 */
export function useSaveState() {
  const queryClient = useQueryClient();
  const createMutation = usePostApiV1LocationStates();
  const updateMutation = usePutApiV1LocationStatesId();

  const invalidate = () => queryClient.invalidateQueries(getApiV1LocationStatesQueryOptions());

  const handleError = (error: unknown, callbacks?: SaveStateCallbacks) => {
    if (isAxiosError(error) && error.response?.status === 409) {
      callbacks?.onConflict?.();
    } else {
      callbacks?.onError?.();
    }
  };

  /** Salva um estado. Cria quando `id` é `undefined`; caso contrário, atualiza. */
  const save = (
    values: StateFormValues,
    id: number | undefined,
    callbacks?: SaveStateCallbacks,
  ) => {
    const data = {
      code: values.code,
      name: values.name,
      abbreviation: values.abbreviation,
      countryId: values.countryId,
    };
    const onSuccess = () => {
      void invalidate();
      callbacks?.onSuccess?.();
    };

    if (id === undefined) {
      createMutation.mutate(
        { data },
        { onSuccess, onError: error => handleError(error, callbacks) },
      );
    } else {
      updateMutation.mutate(
        { id, data },
        { onSuccess, onError: error => handleError(error, callbacks) },
      );
    }
  };

  return {
    save,
    isPending: createMutation.isPending || updateMutation.isPending,
  } as const;
}
